/**
 * Geometry for the filter-knob histograms: mapping pointer positions to
 * buckets, a brushed bucket span to a value range, and a value range back to
 * the buckets to highlight. Pure math, tested without a DOM.
 *
 * Bucket i covers [edges[i], edges[i+1]), except the last, which includes its
 * upper edge — mirroring the server's width_bucket layout in tx-facets.ts.
 */

/** Bucket index under a pointer x offset, clamped into [0, nBuckets - 1]. */
export function bucketAtX(xPx: number, widthPx: number, nBuckets: number): number {
  if (widthPx <= 0 || nBuckets <= 0) return 0;
  const i = Math.floor((xPx / widthPx) * nBuckets);
  return Math.min(Math.max(i, 0), nBuckets - 1);
}

/** Edges that are one bucket per integer (the small-span layout): [3, 4, 5, …]. */
export function isUnitEdges(edges: number[]): boolean {
  return edges.length >= 2 && edges.every((e, i) => Number.isInteger(e) && (i === 0 || e - edges[i - 1] === 1));
}

/**
 * The inclusive integer value range covered by a brushed bucket span. Handles
 * reversed drags. Unit-integer edges snap exactly (bucket [3,4) means the
 * value 3); continuous edges round inward to integers, since every dimension
 * is integer-valued.
 */
export function brushToRange(edges: number[], i0: number, i1: number): { min: number; max: number } | null {
  const n = edges.length - 1;
  if (n < 1) return null;
  const lo = Math.min(Math.max(Math.min(i0, i1), 0), n - 1);
  const hi = Math.min(Math.max(Math.max(i0, i1), 0), n - 1);
  if (isUnitEdges(edges)) {
    return { min: edges[lo], max: edges[hi + 1] - 1 };
  }
  return { min: Math.ceil(edges[lo]), max: Math.floor(edges[hi + 1]) };
}

/**
 * Integer-valued number → decimal string query param. String() would render
 * wei-scale fee edges as "1e+21", which the server rejects as malformed.
 */
export function intToParam(v: number): string {
  return BigInt(v).toString();
}

/**
 * Buckets to highlight for a value range: every bucket the range overlaps.
 * Inverse of brushToRange on unit and wide-linear edges; on log edges narrow
 * low buckets can make the highlight one bucket tighter than the drag, which
 * is fine — the range text is the source of truth.
 */
export function rangeToBrush(edges: number[], min?: number, max?: number): [number, number] | null {
  const n = edges.length - 1;
  if (n < 1 || (min === undefined && max === undefined)) return null;

  const unit = isUnitEdges(edges);
  let i0 = 0;
  if (min !== undefined) {
    while (i0 < n - 1 && edges[i0 + 1] <= min) i0++;
  }
  let i1 = n - 1;
  if (max !== undefined) {
    // Continuous edges: a max exactly on a bucket's lower edge came from a
    // brush that ended one bucket earlier (brushToRange floors the upper
    // edge), so exclude that bucket to make the highlight an exact inverse.
    while (i1 > 0 && (unit ? edges[i1] > max : edges[i1] >= max)) i1--;
  }
  if (i1 < i0) return [i0, i0];
  return [i0, i1];
}
